import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

import CorrectSlide from '../../../assets/correct-slide.png';
import CorrectValve from '../../../assets/correct-valve.png';
import EmptyCorrectValve from '../../../assets/empty-correct-valve.png';
import HelpButtonImage from '../../../assets/help-button.png';
import IncorrectValve from '../../../assets/incorrect-valve.png';
import Logo from '../../../assets/logo.png';
import optionsButtonImage from '../../../assets/options-button.png';
import BackButton from '../../../components/BackButton/BackButton';
import Instrument from '../../../components/Instrument/Instrument';
import Loading from '../../../components/Loading/Loading';
import MusicNote from '../../../components/MusicNote/MusicNote';
import Options from '../../../components/Options/Options';
import Stopwatch from '../../../components/Stopwatch/Stopwatch';
import { brassDict } from '../../../utils/Dictionaries/brassDict';
import { notes } from '../../../utils/Dictionaries/brassNotes';
import { notesWithoutFlats } from '../../../utils/Dictionaries/brassNotesWithoutFlats';
import { keySignatures } from '../../../utils/Dictionaries/keySignatures';
import Styles from './Sandbox.module.css';

const Sandbox = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [resetStopwatch, setResetStopwatch] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [incorrect, setIncorrect] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [showOptions, setShowOptions] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(() => {
    try {
      return localStorage.getItem('membershipStatus') || 'Basic';
    } catch (e) {
      return 'Basic';
    }
  });
  const [isAllowed, setIsAllowed] = useState(false);

  const navigate = useNavigate();

  const [stats, setStats] = useState({
    E1: [0, 0],
    'E#1': [0, 0],
    Fb1: [0, 0],
    F1: [0, 0],
    'F#1': [0, 0],
    Gb1: [0, 0],
    G1: [0, 0],
    'G#1': [0, 0],
    Ab1: [0, 0],
    A1: [0, 0],
    'A#1': [0, 0],
    Bb1: [0, 0],
    B1: [0, 0],
    'B#1': [0, 0],
    Cb2: [0, 0],
    C2: [0, 0],
    'C#2': [0, 0],
    Db2: [0, 0],
    D2: [0, 0],
    'D#2': [0, 0],
    Eb2: [0, 0],
    E2: [0, 0],
    'E#2': [0, 0],
    Fb2: [0, 0],
    F2: [0, 0],
    'F#2': [0, 0],
    Gb2: [0, 0],
    G2: [0, 0],
    'G#2': [0, 0],
    Ab2: [0, 0],
    A2: [0, 0],
    'A#2': [0, 0],
    Bb2: [0, 0],
    B2: [0, 0],
    'B#2': [0, 0],
    Cb3: [0, 0],
    C3: [0, 0],
    'C#3': [0, 0],
    Db3: [0, 0],
    D3: [0, 0],
    'D#3': [0, 0],
    Eb3: [0, 0],
    E3: [0, 0],
    'E#3': [0, 0],
    Fb3: [0, 0],
    F3: [0, 0],
    'F#3': [0, 0],
    Gb3: [0, 0],
    G3: [0, 0],
    'G#3': [0, 0],
    Ab3: [0, 0],
    A3: [0, 0],
    'A#3': [0, 0],
    Bb3: [0, 0],
    B3: [0, 0],
    'B#3': [0, 0],
    Cb4: [0, 0],
    C4: [0, 0],
    'C#4': [0, 0],
    Db4: [0, 0],
    D4: [0, 0],
    'D#4': [0, 0],
    Eb4: [0, 0],
    E4: [0, 0],
    'E#4': [0, 0],
    Fb4: [0, 0],
    F4: [0, 0],
    'F#4': [0, 0],
    Gb4: [0, 0],
    G4: [0, 0],
    'G#4': [0, 0],
    Ab4: [0, 0],
    A4: [0, 0],
    'A#4': [0, 0],
    Bb4: [0, 0],
    B4: [0, 0],
    'B#4': [0, 0],
    Cb5: [0, 0],
    C5: [0, 0],
    'C#5': [0, 0],
    Db5: [0, 0],
    D5: [0, 0],
    'D#5': [0, 0],
    Eb5: [0, 0],
    E5: [0, 0],
    'E#5': [0, 0],
    Fb5: [0, 0],
    F5: [0, 0],
    'F#5': [0, 0],
    Gb5: [0, 0],
    G5: [0, 0],
    'G#5': [0, 0],
    Ab5: [0, 0],
    A5: [0, 0],
    'A#5': [0, 0],
    Bb5: [0, 0],
    B5: [0, 0],
    'B#5': [0, 0],
    Cb6: [0, 0],
    C6: [0, 0],
  });

  // Load options from storage
  const loadOptions = () => {
    const storedData = JSON.parse(localStorage.getItem('options'));
    if (storedData) {
      return storedData;
    }
    return {
      selectedInstrument: 'trumpet',
      selectedRange: 'custom',
      slider1Value: 0,
      slider2Value: notesWithoutFlats['trumpet'].length - 1,
      keyLetter: 'none',
      accidental: 'natural',
      mode: 'major',
      volume: 50,
    };
  };

  const [selectedInstrument, setSelectedInstrument] = useState(
    loadOptions().selectedInstrument,
  );
  const [selectedRange, setSelectedRange] = useState(
    loadOptions().selectedRange,
  );
  const [slider1Value, setSlider1Value] = useState(loadOptions().slider1Value);
  const [slider2Value, setSlider2Value] = useState(loadOptions().slider2Value);
  const [keyLetter, setKeyLetter] = useState(loadOptions().keyLetter);
  const [accidental, setAccidental] = useState(loadOptions().accidental);
  const [mode, setMode] = useState(loadOptions().mode);
  const [volume, setVolume] = useState(loadOptions().volume);

  // Stopwatch
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  const handleElapsedChange = useCallback((t) => {
    setElapsedTime(t);
  }, []);

  function formatTime(timeInMs) {
    let minutes = Math.floor((timeInMs / (1000 * 60)) % 60);
    let seconds = Math.floor((timeInMs / 1000) % 60);
    let milliseconds = Math.floor((timeInMs % 1000) / 10);

    minutes = String(minutes).padStart(2, '0');
    seconds = String(seconds).padStart(2, '0');
    milliseconds = String(milliseconds).padStart(2, '0');

    return `${minutes}:${seconds}:${milliseconds}`;
  }

  useEffect(() => {
    setElapsedTime(0);
    setIsTimerRunning(false);
  }, [resetStopwatch]);

  // Handle random note
  const getRandomInt = (min, max, instr = selectedInstrument) => {
    // Swap min and max if they're incorrect
    if (max < min) {
      let temp = min;
      min = max;
      max = temp;
    }

    // Translate index for "no flats" to "with flats"
    min = notes[instr].indexOf(notesWithoutFlats[instr][min]);
    max = notes[instr].indexOf(notesWithoutFlats[instr][max]);

    if (notes[instr][max].includes('#')) max++;
    const res = Math.floor(Math.random() * (max - min + 1)) + min;
    return res;
  };

  // Check if a note is excluded (Cb, Fb, B#, E#)
  const isExcludedNote = (note) => {
    const noteWithoutOctave = note.substring(0, note.length - 1);
    return ['Cb', 'Fb', 'B#', 'E#'].includes(noteWithoutOctave);
  };

  // Get initial note index, skipping excluded notes
  const getInitialNoteIndex = () => {
    let randomInt = getRandomInt(slider1Value, slider2Value);
    let attempts = 0;
    const maxAttempts = 100;
    while (
      isExcludedNote(notes[selectedInstrument][randomInt]) &&
      attempts < maxAttempts
    ) {
      randomInt = getRandomInt(slider1Value, slider2Value);
      attempts++;
    }
    return randomInt;
  };

  const [noteIndex, setNoteIndex] = useState(getInitialNoteIndex());

  const changeNote = () => {
    // Read options fresh from storage each time to avoid using stale state
    const opts = loadOptions();
    const si = opts.selectedInstrument;
    let s1 = opts.slider1Value;
    let s2 = opts.slider2Value;

    let randomInt = getRandomInt(s1, s2, si);
    let currentNote = notes[si][randomInt];
    currentNote = currentNote.substring(0, currentNote.length - 1);
    let keySignature = opts.keyLetter;
    if (opts.keyLetter !== 'none') {
      if (opts.accidental === 'sharp') keySignature += '#';
      else if (opts.accidental === 'flat') keySignature += 'b';
      if (opts.mode === 'minor') keySignature += 'm';
    }
    let attempts = 0;
    const maxAttempts = 100;
    // Keep trying until we find a valid note (not excluded and matching key signature if needed)
    while (attempts < maxAttempts) {
      if (isExcludedNote(notes[si][randomInt])) {
        randomInt = getRandomInt(s1, s2, si);
        currentNote = notes[si][randomInt];
        currentNote = currentNote.substring(0, currentNote.length - 1);
      } else if (opts.keyLetter !== 'none' && keySignature in keySignatures) {
        if (!keySignatures[keySignature].includes(currentNote)) {
          randomInt = getRandomInt(s1, s2, si);
          currentNote = notes[si][randomInt];
          currentNote = currentNote.substring(0, currentNote.length - 1);
        } else {
          break;
        }
      } else {
        break;
      }
      attempts++;
    }
    setNoteIndex(randomInt);
  };

  // Handle reset
  const reset = () => {
    setResetStopwatch((prev) => prev + 1);
    setCorrect(0);
    setIncorrect(0);
    changeNote();
  };

  useEffect(() => {
    reset();
  }, [
    selectedInstrument,
    selectedRange,
    slider1Value,
    slider2Value,
    keyLetter,
    accidental,
    mode,
  ]);

  const handleGraphData = async (e) => {
    const url = 'https://brassflows.pythonanywhere.com/api/results';
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ stats }),
    };

    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      return data.graph;
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }
  };

  // Handle finish
  const finish = async () => {
    if (correct + incorrect === 0) return;

    const graph = await handleGraphData();

    const formattedTime = formatTime(elapsedTime);
    const resultsData = {
      formattedTime,
      cards: correct + incorrect,
      accuracy,
      graph,
      selectedRange,
      correct: !isAllowed && membershipStatus === 'Basic' ? 0 : correct,
    };
    localStorage.setItem('localResults', JSON.stringify(resultsData));

    navigate('/classroom/sandbox/results');
  };

  const toggleOptions = () => {
    setShowOptions((prev) => {
      const newVal = !prev;
      // If we're closing the options panel, reload options from localStorage
      if (prev === true && newVal === false) {
        const opts = loadOptions();
        setSelectedInstrument(opts.selectedInstrument);
        setSelectedRange(opts.selectedRange);
        setSlider1Value(opts.slider1Value);
        setSlider2Value(opts.slider2Value);
        setKeyLetter(opts.keyLetter);
        setAccidental(opts.accidental);
        setMode(opts.mode);
        setVolume(opts.volume);

        changeNote();
      }
      return newVal;
    });
  };

  // Update localResults
  useEffect(() => {
    setAccuracy(((correct / (correct + incorrect)) * 100).toFixed(1));
  }, [correct, incorrect]);

  const [showCorrectValve1, setShowCorrectValve1] = useState(null);
  const [showCorrectValve2, setShowCorrectValve2] = useState(null);
  const [showCorrectValve3, setShowCorrectValve3] = useState(null);
  const [showCorrectValve4, setShowCorrectValve4] = useState(null);
  const [showCorrectTrigger, setShowCorrectTrigger] = useState(null);
  const [showCorrectSlide, setShowCorrectSlide] = useState(null);
  const [correctSlideDistance, setCorrectSlideDistance] = useState(0);
  const correctSlideNumber = Number(
    brassDict[selectedInstrument][notes[selectedInstrument][noteIndex]][0],
  );

  // Feedback overlay
  const [feedbackType, setFeedbackType] = useState(null); // 'correct' | 'incorrect' | null
  const feedbackTimerRef = useRef(null);

  const showFeedback = (type, duration = 300) => {
    setFeedbackType(type);
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
    }
    feedbackTimerRef.current = setTimeout(() => {
      setFeedbackType(null);
      feedbackTimerRef.current = null;
    }, duration);
  };

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  // Log whenever the feedback overlay type changes (helps show when overlay appears)
  useEffect(() => {
    if (feedbackType) {
      try {
        // eslint-disable-next-line no-console
        console.debug('feedbackType changed', feedbackType);
      } catch (e) {
        // ignore
      }
    }
  }, [feedbackType]);

  const handleIncorrect = (showCorrectAnswer, pressedKeys = []) => {
    // Trace call origin and parameters
    try {
      // eslint-disable-next-line no-console
      console.trace('handleIncorrect called', {
        showCorrectAnswer,
        pressedKeys,
      });
    } catch (e) {
      // ignore
    }
    // Use the first combo unconditionally as the "correct" fingering (index 0)
    const combos =
      brassDict[selectedInstrument][notes[selectedInstrument][noteIndex]] || [];
    let correctKeys = [];
    if (combos.length > 0) {
      const first = combos[0];
      if (typeof first === 'string') {
        correctKeys = first.split('');
      } else if (Array.isArray(first)) {
        correctKeys = first.map((k) => String(k));
      } else {
        correctKeys = [];
      }
    }

    // When showing wrong answer, include pressedKeys and intended display for diagnostics
    if (showCorrectAnswer) {
      showFeedback('incorrect', 2000);
    }

    if (['trumpet', 'euphonium', 'tuba'].includes(selectedInstrument)) {
      if (showCorrectAnswer) {
        if (correctKeys.includes('j')) {
          setShowCorrectValve1(1);
        } else {
          setShowCorrectValve1(0);
        }
        if (correctKeys.includes('k')) {
          setShowCorrectValve2(1);
        } else {
          setShowCorrectValve2(0);
        }
        if (correctKeys.includes('l')) {
          setShowCorrectValve3(1);
        } else {
          setShowCorrectValve3(0);
        }
        if (correctKeys.includes(';')) {
          setShowCorrectValve4(1);
        } else {
          setShowCorrectValve4(0);
        }
      } else {
        setShowCorrectValve1(null);
        setShowCorrectValve2(null);
        setShowCorrectValve3(null);
        setShowCorrectValve4(null);
      }
    } else if (selectedInstrument === 'frenchHorn') {
      if (showCorrectAnswer) {
        if (correctKeys.includes('q')) {
          setShowCorrectValve1(1);
        } else {
          setShowCorrectValve1(0);
        }
        if (correctKeys.includes('w')) {
          setShowCorrectValve2(1);
        } else {
          setShowCorrectValve2(0);
        }
        if (correctKeys.includes('e')) {
          setShowCorrectValve3(1);
        } else {
          setShowCorrectValve3(0);
        }
        if (correctKeys.includes('c')) {
          setShowCorrectTrigger(1);
        } else {
          setShowCorrectTrigger(0);
        }
      } else {
        setShowCorrectValve1(null);
        setShowCorrectValve2(null);
        setShowCorrectValve3(null);
        setShowCorrectTrigger(null);
      }
    } else {
      if (correctKeys.includes('1')) {
        setCorrectSlideDistance(736);
      } else if (correctKeys.includes('2')) {
        setCorrectSlideDistance(660);
      } else if (correctKeys.includes('3')) {
        setCorrectSlideDistance(586);
      } else if (correctKeys.includes('4')) {
        setCorrectSlideDistance(513);
      } else if (correctKeys.includes('5')) {
        setCorrectSlideDistance(439);
      } else if (correctKeys.includes('6')) {
        setCorrectSlideDistance(363);
      } else if (correctKeys.includes('7')) {
        setCorrectSlideDistance(290);
      }
      setShowCorrectSlide(showCorrectAnswer);
    }
  };

  const handleAnswer = async (isCorrect) => {
    const currentNote = notes[selectedInstrument][noteIndex];
    if (isCorrect) {
      if (!isTimerRunning) {
        setElapsedTime(0);
        setIsTimerRunning(true);
      }

      // show subtle green overlay
      showFeedback('correct');
      // Play sound
      const safeNote = currentNote.replace('#', 's');
      const sound = new Audio(`/sounds/${selectedInstrument}/${safeNote}.mp3`);
      sound.volume = volume / 100;
      sound.load();
      sound.play();

      setStats((prevStats) => ({
        ...prevStats,
        [currentNote]: [
          prevStats[currentNote][0] + 1,
          prevStats[currentNote][1],
        ],
      }));
      setCorrect((prev) => prev + 1);
    } else {
      setStats((prevStats) => ({
        ...prevStats,
        [currentNote]: [
          prevStats[currentNote][0],
          prevStats[currentNote][1] + 1,
        ],
      }));
      setIncorrect((prev) => prev + 1);
    }

    changeNote();
  };

  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      exitOnEsc: true,
    },
  });

  if (selectedInstrument === 'trumpet' || selectedInstrument === 'euphonium') {
    tour.addStep({
      id: 'step-1',
      text: 'Use J, K, and L to press down the valves, and space bar for open fingerings.',
      attachTo: {
        element: '.instrument',
        on: 'bottom',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  } else if (selectedInstrument === 'frenchHorn') {
    tour.addStep({
      id: 'step-1',
      text: 'Use Q, W, E, and C to press down the valves and trigger, and space bar for open fingerings.',
      attachTo: {
        element: '.instrument',
        on: 'top',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  } else if (selectedInstrument === 'tuba') {
    tour.addStep({
      id: 'step-1',
      text: 'Use J, K, L, and ; to press down the valves, and space bar for open fingerings.',
      attachTo: {
        element: '.instrument',
        on: 'bottom',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  } else {
    tour.addStep({
      id: 'step-1',
      text: 'Use the numbers 2-7 to change the slide position, and space bar for first position.',
      attachTo: {
        element: '.instrument',
        on: 'bottom',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  }

  const toggleHelp = () => {
    tour.start();
  };

  const handleLoading = (loading) => {
    setIsLoading(loading);
  };

  return (
    <div>
      {isLoading && <Loading />}
      <div
        className={`${Styles.pageWrapper} ${
          showOptions ? Styles.darkerBackground : ''
        }`}
        style={{
          opacity: isLoading ? 0 : 1,
        }}
      >
        <div className={Styles.subButtonsContainer}>
          <BackButton destination={'classroom'} />
          <button className={Styles.options} onClick={toggleOptions}>
            <img src={optionsButtonImage} alt="Options button" />
          </button>
          <button
            className={Styles.help}
            onClick={toggleHelp}
            onKeyDown={(e) => {
              if (e.key === ' ') {
                e.preventDefault();
              }
            }}
          >
            <img src={HelpButtonImage} alt="Help button" />
          </button>
        </div>
        <div className={Styles.practiceWrapper}>
          <div
            className={`${Styles.feedbackOverlay} ${
              feedbackType
                ? feedbackType === 'correct'
                  ? Styles.correct
                  : Styles.incorrect
                : ''
            } ${feedbackType ? Styles.show : ''}`}
          />
          <h4 className={Styles.noteName}>
            {notes[selectedInstrument][noteIndex]}
          </h4>
          <div className={Styles.musicDisplay}>
            <div className={Styles.musicNoteContainer}>
              <MusicNote
                className={Styles.musicNote}
                note={notes[selectedInstrument][noteIndex]}
                selectedInstrument={selectedInstrument}
              />
            </div>
            <div className={`${Styles.instrument} instrument`}>
              <Instrument
                correctKeys={
                  brassDict[selectedInstrument][
                    notes[selectedInstrument][noteIndex]
                  ]
                }
                handleIncorrect={handleIncorrect}
                handleAnswer={handleAnswer}
                instrument={selectedInstrument}
                handleLoading={handleLoading}
                mode={'Sandbox'}
              />
            </div>
          </div>
          <div className={Styles.dashboard}>
            <div className={Styles.logoContainer}>
              <img src={Logo} alt={'Website logo'} />
            </div>
            <Stopwatch
              resetStopwatch={resetStopwatch}
              running={isTimerRunning}
              onElapsedChange={handleElapsedChange}
            />
            <div className={Styles.cardStats}>
              <div className={Styles.individualStat}>
                <h3>correct</h3>
                <h2>{correct}</h2>
              </div>
              <div className={Styles.individualStat}>
                <h3>incorrect</h3>
                <h2>{incorrect}</h2>
              </div>
              <div className={Styles.individualStat}>
                <h3>accuracy</h3>
                <h2>{correct === 0 && incorrect === 0 ? '0.0' : accuracy}%</h2>
              </div>
            </div>
            <div className={Styles.buttonsContainer}>
              <button
                className={Styles.button}
                onClick={reset}
                onKeyDown={(event) => {
                  if (event.key === ' ') {
                    event.preventDefault();
                  }
                }}
              >
                Reset
              </button>
              <button
                className={Styles.button}
                onClick={finish}
                onKeyDown={(event) => {
                  if (event.key === ' ') {
                    event.preventDefault();
                  }
                }}
              >
                Finish
              </button>
            </div>
          </div>
        </div>
        <div>
          {selectedInstrument === 'trombone' ? (
            <div>
              {showCorrectSlide ? (
                <div
                  className={Styles.slide}
                  style={{ marginRight: `${correctSlideDistance}px` }}
                >
                  <img src={CorrectSlide} alt={'Correct slide'} />
                  <div className={Styles.slideNumberContainer}>
                    <p>{correctSlideNumber}</p>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          ) : selectedInstrument === 'tuba' ? (
            <div className={Styles.rotaryValves}>
              {showCorrectValve1 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve1 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve2 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve2 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve3 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve3 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve4 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve4 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
            </div>
          ) : (
            <div
              className={
                selectedInstrument === 'frenchHorn'
                  ? Styles.rotaryValves
                  : Styles.valves
              }
            >
              {showCorrectValve1 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve1 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve2 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve2 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve3 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve3 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectTrigger === 1 ? (
                <img
                  className={Styles.trigger}
                  src={CorrectValve}
                  alt={'Show correct trigger'}
                />
              ) : showCorrectTrigger === 0 ? (
                <img
                  className={Styles.trigger}
                  src={IncorrectValve}
                  alt={'Show incorrect trigger'}
                />
              ) : (
                <img
                  className={Styles.emptyTrigger}
                  src={EmptyCorrectValve}
                  alt={'Empty correct trigger'}
                />
              )}
            </div>
          )}
        </div>
        {showOptions && <Options toggleOptions={toggleOptions} />}
      </div>
    </div>
  );
};

export default Sandbox;
